import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Car, Pencil, Trash2, Loader2, Plus, X, Power, RotateCcw, Upload, ShieldCheck } from "lucide-react";
import { fetchAdminVehicles, createVehicle, updateVehicle, deleteVehicle } from "@/lib/api";

const emptyForm = { name: "", capacity: "", requires_licence: "no", status: "active", image_url: "" };
const MAX_BYTES = 10 * 1024 * 1024;

// Downscale to keep the stored image light while preserving aspect ratio (no distortion).
const fileToScaledDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1024;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const isPng = file.type === "image/png";
        resolve(canvas.toDataURL(isPng ? "image/png" : "image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-[#0F172A] outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]";

export const VehicleManagementTab = ({ onChanged }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [deleteRec, setDeleteRec] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [processingImg, setProcessingImg] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVehicles(await fetchAdminVehicles());
    } catch {
      toast.error("Failed to load vehicles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      toast.error("Image must be a JPG or PNG file");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Image exceeds the 10MB limit");
      e.target.value = "";
      return;
    }
    setProcessingImg(true);
    try {
      const dataUrl = await fileToScaledDataUrl(file);
      set("image_url", dataUrl);
    } catch {
      toast.error("Could not read the image");
    } finally {
      setProcessingImg(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const capacity = Number(form.capacity);
    if (!name) return toast.error("Vehicle name is required");
    if (!Number.isInteger(capacity) || capacity < 1) return toast.error("Passenger capacity must be a positive whole number");
    const payload = {
      name,
      capacity,
      requires_licence: form.requires_licence === "yes",
      status: form.status,
      image_url: form.image_url || null,
    };
    setSaving(true);
    try {
      if (editId) {
        await updateVehicle(editId, payload);
        toast.success("Vehicle updated");
      } else {
        await createVehicle(payload);
        toast.success("Vehicle added");
      }
      resetForm();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save vehicle");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (v) => {
    setEditId(v.id);
    setForm({
      name: v.name,
      capacity: String(v.capacity),
      requires_licence: v.requires_licence ? "yes" : "no",
      status: v.status,
      image_url: v.image_url || "",
    });
    if (fileRef.current) fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleStatus = async (v) => {
    try {
      await updateVehicle(v.id, { status: v.status === "active" ? "inactive" : "active" });
      toast.success(v.status === "active" ? "Vehicle deactivated" : "Vehicle activated");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update status");
    }
  };

  const reactivate = async (v) => {
    try {
      await updateVehicle(v.id, { status: "active" });
      toast.success("Vehicle restored");
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not restore vehicle");
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteVehicle(deleteRec.id);
      toast.success("Vehicle removed from operations");
      if (editId === deleteRec.id) resetForm();
      setDeleteRec(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete vehicle");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="vehicles-tab">
      {/* Form */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-[#0F172A]">
          <Car className="h-5 w-5 text-[#5C2D91]" />
          {editId ? "Edit Vehicle" : "Add Vehicle"}
        </h2>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Vehicle name</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Buggy 6-Seater" className={inputCls} data-testid="veh-name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Passenger capacity</label>
              <input type="number" min={1} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} placeholder="e.g. 6" className={inputCls} data-testid="veh-capacity" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Driving licence required</label>
              <select value={form.requires_licence} onChange={(e) => set("requires_licence", e.target.value)} className={inputCls} data-testid="veh-licence">
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Status</label>
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="veh-status">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-[#475569]">Vehicle image (JPG / PNG, max 10MB)</label>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png" onChange={onPickImage} className="hidden" data-testid="veh-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-slate-50" data-testid="veh-image-pick">
                {processingImg ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Choose image
              </button>
            </div>
            {form.image_url && (
              <div className="flex items-center gap-2">
                <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                  <img src={form.image_url} alt="Vehicle preview" className="h-full w-full object-contain" data-testid="veh-image-preview" />
                </div>
                <button type="button" onClick={() => { set("image_url", ""); if (fileRef.current) fileRef.current.value = ""; }} className="rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50" data-testid="veh-image-clear" title="Remove image">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving} className="flex items-center justify-center gap-1.5 rounded-lg bg-[#5C2D91] px-6 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60" data-testid="veh-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editId ? "Update Vehicle" : "Add Vehicle"}
            </button>
            {editId && (
              <button type="button" onClick={resetForm} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#64748B] hover:bg-slate-50" data-testid="veh-cancel-edit">
                <X className="h-4 w-4" /> Cancel
              </button>
            )}
          </div>
        </form>
      </section>

      {/* List */}
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="vehicles-list">
        <h2 className="mb-4 font-heading text-lg font-semibold text-[#0F172A]">Fleet Vehicles</h2>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-[#64748B]">
            <Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading vehicles…
          </div>
        ) : vehicles.length === 0 ? (
          <p className="py-8 text-center text-sm text-[#64748B]" data-testid="veh-empty">No vehicles yet. Add your first vehicle above.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="mobile-cards w-full min-w-[820px] text-left text-sm" data-testid="veh-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-[#64748B]">
                  <th className="py-3 pr-4">Image</th>
                  <th className="py-3 pr-4">Name</th>
                  <th className="py-3 pr-4">Capacity</th>
                  <th className="py-3 pr-4">Licence</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-b border-slate-100 last:border-0" data-testid={`veh-row-${v.id}`}>
                    <td data-label="Image" className="py-3 pr-4">
                      <div className="flex h-10 w-16 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                        {v.image_url ? <img src={v.image_url} alt={v.name} className="h-full w-full object-contain" /> : <Car className="h-5 w-5 text-slate-400" />}
                      </div>
                    </td>
                    <td data-label="Name" className="py-3 pr-4 font-medium text-[#0F172A]">{v.name}</td>
                    <td data-label="Capacity" className="py-3 pr-4 text-[#0F172A]">{v.capacity} pax</td>
                    <td data-label="Licence" className="py-3 pr-4">
                      {v.requires_licence ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#5C2D91]/10 px-2 py-0.5 text-xs font-semibold text-[#5C2D91]"><ShieldCheck className="h-3.5 w-3.5" /> Required</span>
                      ) : (
                        <span className="text-xs text-[#64748B]">Not required</span>
                      )}
                    </td>
                    <td data-label="Status" className="py-3 pr-4">
                      {v.deleted ? (
                        <span className="inline-block rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600 ring-1 ring-rose-200" data-testid={`veh-status-${v.id}`}>Deleted</span>
                      ) : v.status === "active" ? (
                        <span className="inline-block rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200" data-testid={`veh-status-${v.id}`}>Active</span>
                      ) : (
                        <span className="inline-block rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200" data-testid={`veh-status-${v.id}`}>Inactive</span>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => startEdit(v)} className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-[#4A2475]" data-testid={`veh-edit-${v.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Edit
                        </button>
                        {v.deleted ? (
                          <button onClick={() => reactivate(v)} className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-all hover:bg-emerald-100" data-testid={`veh-restore-${v.id}`}>
                            <RotateCcw className="h-3.5 w-3.5" /> Restore
                          </button>
                        ) : (
                          <>
                            <button onClick={() => toggleStatus(v)} className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-[#0F172A] transition-all hover:bg-slate-50" data-testid={`veh-toggle-${v.id}`}>
                              <Power className="h-3.5 w-3.5" /> {v.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                            <button onClick={() => setDeleteRec(v)} className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-100" data-testid={`veh-delete-${v.id}`}>
                              <Trash2 className="h-3.5 w-3.5" /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Delete confirmation */}
      {deleteRec && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="veh-delete-modal">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-[#0F172A]">Delete vehicle?</h3>
            <p className="mt-2 text-sm text-[#64748B]">
              <span className="font-semibold text-[#0F172A]">{deleteRec.name}</span> will be removed from all Availability Overview sections, booking and Maintenance / Charging dropdowns. Its booking and maintenance history is preserved, and you can restore it any time.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDeleteRec(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#64748B] hover:bg-slate-50" data-testid="veh-delete-cancel">Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-red-700 disabled:opacity-60" data-testid="veh-delete-confirm">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
